import React from 'react';
import { Message, Config, Conversation } from '../../types';
import { getCompletion, saveConversation } from '../../services/api';
import { parseMessage } from '../../utils/messageParser';
import { useAuthToken } from '../../services/auth';
import './ChatWindow.css';
import { ChatMessages } from './ChatMessages';
import { Settings } from '../Settings/Settings';
import { v4 as uuidv4 } from 'uuid';

interface Props {
  config: Config;
  input: string;
  setInput: (input: string) => void;
  shouldSubmit: boolean;
  setShouldSubmit: (shouldSubmit: boolean) => void;
  onOpenLibrary: () => void;
  onOpenPdfViewer: (url: string) => void;
  messages: Message[];
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  currentChatId: string | null;
  setCurrentChatId: (chatId: string | null) => void;
  showSettings: boolean;
  setShowSettings: (show: boolean) => void;
  onSaveSettings: (apiUrl: string) => void;
  onUpdateChatHistory?: () => void;
  userId: string;
}

function ChatWindow({ 
  config, 
  input, 
  setInput, 
  shouldSubmit, 
  setShouldSubmit,
  onOpenLibrary,
  onOpenPdfViewer,
  messages,
  setMessages,
  currentChatId,
  setCurrentChatId,
  showSettings,
  setShowSettings,
  onSaveSettings,
  onUpdateChatHistory,
  userId
}: Props) {
  const [isLoading, setIsLoading] = React.useState(false);
  const [apiUrl, setApiUrl] = React.useState(config.apiUrl);
  const { getToken, account } = useAuthToken();

  // Auto-save conversation when messages change
  React.useEffect(() => {
    if (currentChatId && messages.length > 0) {
      const saveTimer = setTimeout(async () => {
        try {
          const token = await getToken();
          const conversation: Conversation = {
            id: currentChatId,
            title: messages[0].content.slice(0, 50), // Use first message as title
            messages,
            lastUpdated: new Date().toISOString()
          };
          
          await saveConversation(conversation, {
            token: token || undefined,
            userId,
            apiUrl: config.apiUrl
          });
          
          if (onUpdateChatHistory) {
            onUpdateChatHistory();
          }
        } catch (error) {
          console.error('Error saving conversation:', error);
        }
      }, 1000); // Save after 1 second of no changes

      return () => clearTimeout(saveTimer);
    }
  }, [messages, currentChatId, userId, config.apiUrl, getToken, onUpdateChatHistory]);

  // Update apiUrl when config changes
  React.useEffect(() => {
    setApiUrl(config.apiUrl);
  }, [config.apiUrl]);

  const handleSendMessage = React.useCallback(async () => {
    if (!input.trim()) return;

    // Generate new chat ID if this is a new conversation
    const chatId = currentChatId || uuidv4();
    if (!currentChatId) {
      setCurrentChatId(chatId);
    }

    const userMessage: Message = {
      role: 'user',
      content: input,
      citations: [],
      followupQuestions: []
    };

    setMessages((prev: Message[]) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const token = await getToken();
      
      // Ensure userId is valid
      const effectiveUserId = userId || account?.username || 'anonymous';
      if (!userId) {
        console.warn('No userId provided for content filtering, using fallback:', effectiveUserId);
      }
      
      const completion = getCompletion({
        messages: [...messages, userMessage],
        apiUrl: config.apiUrl,
        token: token || undefined,
        userId: effectiveUserId
      });

      // Initialize assistant message
      setMessages((prev: Message[]) => [
        ...prev,
        {
          role: 'assistant',
          content: '',
          citations: [],
          followupQuestions: []
        }
      ]);

      for await (const chunk of completion) {
        console.log('Received chunk:', chunk); // Debug log

        if (chunk.delta) {
          setMessages((prev: Message[]) => {
            const newMessages = [...prev];
            const lastMessage = newMessages[newMessages.length - 1];

            // Update the last message with new content and metadata
            newMessages[newMessages.length - 1] = {
              ...lastMessage,
              content: lastMessage.content + (chunk.delta.content || ''),
              citations: chunk.delta.citations || lastMessage.citations,
              followupQuestions: chunk.delta.followupQuestions || lastMessage.followupQuestions
            };

            return newMessages;
          });
        }
      }
    } catch (error) {
      console.error('Error in chat completion:', error);
      setMessages((prev: Message[]) => [
        ...prev,
        {
          role: 'assistant',
          content: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`,
          citations: [],
          followupQuestions: []
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  }, [input, messages, config, setInput, setMessages, getToken, currentChatId, setCurrentChatId, userId, account]);

  return (
    <div className="main-content">
      <div className="background-logo">
        <img src="/assets/logo.png" alt="" />
      </div>
      <ChatMessages
        messages={messages}
        isLoading={isLoading}
        input={input}
        setInput={setInput}
        onSendMessage={handleSendMessage}
        onCitationClick={onOpenPdfViewer}
        onFollowupClick={setInput}
        config={config}
        userId={userId}
      />
    </div>
  );
}

export default ChatWindow; 